import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "../hooks/useTranslation";
import OAuthButtons from "./OAuthButtons";
import { useToast } from "../contexts/ToastContext";
import AuthShell, { authError, authField, authLabel } from "../v2/pages/AuthShell";
import { Arrow } from "../v2/components/Primitives";

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

/** Live password rule row. */
const Rule: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <li className={`flex items-center gap-2 ${ok ? "text-teal-deep" : "text-ink/45"}`}>
    <span className={`h-px w-3 ${ok ? "bg-teal-deep" : "bg-ink/30"}`} />
    {children}
  </li>
);

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ lang?: string }>();
  const currentLanguage = params.lang || "fr";
  const isFr = currentLanguage !== "en";
  const { success: toastSuccess, error: toastError } = useToast();

  const [formData, setFormData] = useState<SignupFormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof SignupFormData, string>>>({});

  // Password live checks
  const pwd = formData.password || "";
  const hasMin = pwd.length >= 8;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
  const hasNumber = /[0-9]/.test(pwd);
  const confirmMatches =
    (formData.confirmPassword?.length || 0) > 0 && formData.confirmPassword === formData.password;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name as keyof SignupFormData]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof SignupFormData, string>> = {};
    if (!formData.firstName.trim()) newErrors.firstName = t("auth.signup.firstNameRequired");
    else if (formData.firstName.trim().length < 2) newErrors.firstName = t("auth.signup.firstNameTooShort");
    if (!formData.lastName.trim()) newErrors.lastName = t("auth.signup.lastNameRequired");
    else if (formData.lastName.trim().length < 2) newErrors.lastName = t("auth.signup.lastNameTooShort");
    if (!formData.email.trim()) newErrors.email = t("auth.signup.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t("auth.signup.emailInvalid");
    if (!formData.password) newErrors.password = t("auth.signup.passwordRequired");
    else if (!(hasMin && hasUpper && hasLower && hasSpecial && hasNumber)) newErrors.password = t("auth.signup.passwordInvalid");
    if (!formData.confirmPassword) newErrors.confirmPassword = t("auth.signup.confirmPasswordRequired");
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t("auth.signup.confirmPasswordMismatch");
    if (!formData.acceptTerms) newErrors.acceptTerms = t("auth.signup.termsRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      const response = await fetch(`${apiUrl}/auth/register-with-otp?lang=${currentLanguage}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const payload = (data && (data.data || data)) || {};
        const email = payload.email || formData.email.trim();
        const expiresAt = payload.expiresAt;
        toastSuccess(t("auth.signup.createdToast"));
        navigate(
          `/${currentLanguage}/verify-email?email=${encodeURIComponent(email)}${
            expiresAt ? `&expiresAt=${encodeURIComponent(expiresAt)}` : ""
          }`,
        );
      } else {
        if (data.message) {
          if (typeof data.message === "string" && data.message.toLowerCase().includes("email")) {
            setErrors({ email: t("auth.signup.emailInUse") });
            toastError(t("auth.signup.emailInUse"));
          } else {
            toastError(data.message || t("auth.signup.signupError"));
          }
        } else {
          toastError(t("auth.signup.signupError"));
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      toastError(t("auth.signup.serverError"));
    } finally {
      setIsLoading(false);
    }
  };

  const eye = (shown: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      className="absolute inset-y-0 right-0 flex items-center text-ink/40 hover:text-ink"
      aria-label={shown ? "Hide password" : "Show password"}
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <AuthShell
      title={t("auth.signup.title")}
      statement={
        isFr ? (
          <>
            Deux minutes, et vos baskets sont <em className="italic text-tangerine">prises en charge.</em>
          </>
        ) : (
          <>
            Two minutes, and your sneakers are <em className="italic text-tangerine">taken care of.</em>
          </>
        )
      }
      note={
        isFr
          ? "Composez votre prestation, suivez chaque étape, recevez vos paires comme neuves."
          : "Build your service, follow every step, get your pairs back like new."
      }
      description={
        isFr
          ? "Créez votre compte Lave Tes Baskets en deux minutes et composez votre premier nettoyage de baskets, dès 20€ la paire."
          : "Create your Lave Tes Baskets account in two minutes and build your first sneaker cleaning, from €20 per pair."
      }
      img="/illustrations/landing/signup.webp"
    >
      <div className="v2-reveal">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
          {isFr ? "Nouveau compte" : "New account"}
        </div>
        <h1 className="mt-3 font-display text-[clamp(2.2rem,4vw,3.25rem)] leading-[0.98] tracking-[-0.02em]">
          {t("auth.signup.title")}
        </h1>
        <p className="mt-3 text-sm text-ink/60">{t("auth.signup.subtitle")}</p>
      </div>

      <div className="v2-reveal mt-8" data-delay="1">
        <OAuthButtons mode="signup" />
      </div>

      <form onSubmit={handleSubmit} className="v2-reveal mt-6 space-y-7" data-delay="2" noValidate>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className={authLabel}>{t("auth.signup.firstNameLabel")}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={authField}
              placeholder={t("auth.signup.firstNamePlaceholder")}
              autoComplete="given-name"
              required
            />
            {errors.firstName && <p className={authError}>{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className={authLabel}>{t("auth.signup.lastNameLabel")}</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={authField}
              placeholder={t("auth.signup.lastNamePlaceholder")}
              autoComplete="family-name"
              required
            />
            {errors.lastName && <p className={authError}>{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className={authLabel}>{t("auth.signup.emailLabel")}</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={authField}
            placeholder={t("auth.signup.emailPlaceholder")}
            autoComplete="email"
            required
          />
          {errors.email && <p className={authError}>{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className={authLabel}>{t("auth.signup.passwordLabel")}</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              className={`${authField} pr-10`}
              placeholder={t("auth.signup.passwordPlaceholder")}
              autoComplete="new-password"
              required
            />
            {eye(showPassword, () => setShowPassword(!showPassword))}
          </div>
          {errors.password && <p className={authError}>{errors.password}</p>}
          {(passwordFocused || formData.password.length > 0) && (
            <ul className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.12em]">
              <Rule ok={hasMin}>{t("auth.signup.passwordRuleMin").replace(/^•\s*/, "")}</Rule>
              <Rule ok={hasUpper}>{t("auth.signup.passwordRuleUpper").replace(/^•\s*/, "")}</Rule>
              <Rule ok={hasLower}>{t("auth.signup.passwordRuleLower").replace(/^•\s*/, "")}</Rule>
              <Rule ok={hasNumber}>{t("auth.signup.passwordRuleNumber").replace(/^•\s*/, "")}</Rule>
              <Rule ok={hasSpecial}>{t("auth.signup.passwordRuleSpecial").replace(/^•\s*/, "")}</Rule>
            </ul>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className={authLabel}>{t("auth.signup.confirmPasswordLabel")}</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`${authField} pr-10`}
              placeholder={t("auth.signup.confirmPasswordPlaceholder")}
              autoComplete="new-password"
              required
            />
            {eye(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))}
          </div>
          {errors.confirmPassword && <p className={authError}>{errors.confirmPassword}</p>}
          {confirmMatches && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-teal-deep">
              {t("auth.signup.passwordsMatch")}
            </p>
          )}
        </div>

        <div>
          <label className="flex cursor-pointer items-start gap-3 text-sm text-ink/70">
            <input
              type="checkbox"
              id="acceptTerms"
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              className="mt-1 h-4 w-4 flex-shrink-0 accent-[#0B0B0C]"
            />
            <span>
              {`${t("auth.signup.acceptTermsPrefix")} `}
              <Link to={`/${currentLanguage}/terms`} className="v2-link text-ink">
                {t("auth.signup.terms")}
              </Link>{" "}
              {`${t("auth.signup.and")} `}
              <Link to={`/${currentLanguage}/privacy`} className="v2-link text-ink">
                {t("auth.signup.privacy")}
              </Link>
            </span>
          </label>
          {errors.acceptTerms && <p className={authError}>{errors.acceptTerms}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="v2-btn v2-btn-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("auth.signup.submitting")}
            </>
          ) : (
            <>
              {t("auth.signup.submit")}
              <Arrow />
            </>
          )}
        </button>
      </form>

      <p className="v2-reveal mt-8 text-sm text-ink/60" data-delay="3">
        {`${t("auth.signup.haveAccount")} `}
        <Link to={`/${currentLanguage}/login`} className="v2-link text-ink">
          {t("auth.signup.loginCta")}
        </Link>
      </p>
    </AuthShell>
  );
};

export default Signup;
